<template>
  <filter-group
    has-pagination
    :added-filters="filterGroupItems"
    :filter-options="filterOptions"
    :total="total"
    @filter-adding-started="filterAddingStarted"
    @filter-applied="handleFilterApplied"
    @add-similar-filter="(id, f) => $emit('add-similar-filter', id, f)"
    @remove="handleRemove"
    @search-change="search"
    @load-more="fetchNextPage"
  />
</template>

<script lang="ts" setup>
import { cloneDeep } from 'lodash-es'
import { computed, ref } from 'vue'
import useHelpdeskCustomFields from '@/modules/shared/filters/useHelpdeskCustomFields'
import { session } from '@/composables/useSession'

import i18n from '@/i18n'
import { translationExists } from '@/i18n/utils'
import FilterGroup from '@/components/filters/FilterGroup.vue'
import {
  type ValueComponentProps,
  FilterValueType,
  type FilterOption,
  type FilterItemEmit,
  type AddedFilter,
} from '@/components/filters/types'

import type { DropdownOption, DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import {
  fieldsMapToFieldItems,
  getDescriptionKey,
  getDescriptionOptions,
  getEmptyFilterOption,
  getFilterConditionsByOptionName,
} from './utils'

const props = defineProps<{
  addedFilters: DeconstructedOption[]
  options: DefaultFilterOption[]
  selectedOptionNames: string[]
}>()

const emit = defineEmits<{
  'filter-adding-started': [o: FilterOption]
  'filter-applied': [filter: DeconstructedOption]
  'add-similar-filter': [f: FilterItemEmit, o: FilterOption]
  remove: [f: string]
}>()

const HELPDESK_CUSTOM_FIELD = 'TICKET_FIELD'

const filterBeingAdded = ref<DeconstructedOption | null>(null)
const translatedFilterBeingAdded = ref<AddedFilter | null>(null)

const workspaceIds = computed(() => [session.workspace.id])
const helpdeskCustomFieldsOptions = computed(() => ({
  ids: props.addedFilters.map((f) => f.option.name),
}))
const { fieldsMap, search, total, fetchNextPage, selectedFieldsFetched } = useHelpdeskCustomFields(
  workspaceIds,
  helpdeskCustomFieldsOptions,
)

const translateLabel = (opt: DeconstructedOption['option']) => {
  let label = ''
  // this fn is also used for translating conditions, so we need to check if it's a custom field
  if (opt?.isCustom && opt?.name) label = fieldsMap.value?.[opt.name]?.name || ''
  else if (opt?.label) label = translationExists(opt.label) ? i18n.t(opt.label) : opt.label
  return label
}

const addLabel = (opt: DeconstructedOption['option']): FilterOption => {
  // @ts-expect-error not sure if opt can be undefined
  if (!opt || !Object.keys(opt).length) return opt

  const descriptionKey = getDescriptionKey(opt)

  return {
    ...opt,
    name: opt.name || '',
    label: translateLabel(opt),
    description: translationExists(descriptionKey) ? i18n.t(descriptionKey, getDescriptionOptions(opt)) : undefined,
  }
}

const filterOptions = computed(() => {
  const items = fieldsMapToFieldItems(fieldsMap.value)
  const fieldItems = items.sort((a, b) => a.label.localeCompare(b.label))

  return fieldItems.map((o: DropdownOption) => addLabel(o))
})

const toFilterGroupItem = (d: DeconstructedOption) => {
  const descriptionKey = getDescriptionKey(d.option)

  const translatedOption = addLabel(d.option)
  return {
    id: d.id,
    label: translatedOption.label,
    name: translatedOption.name,
    showAddSimilarButton: !!filterOptions.value.find((o) => o?.name === translatedOption.name),
    description: translationExists(descriptionKey)
      ? i18n.t(descriptionKey, getDescriptionOptions(translatedOption))
      : undefined,
    selectedCondition: addLabel(d.condition),
    conditions: getFilterConditions(),
    value: d.values as any,
    valueType: FilterValueType.CustomField,
    valueComponentProps: undefined as ValueComponentProps,
    intelligenceIcon: false,
    botIcon: false,
    disabled: false,
  }
}

const getFilterConditions = () => {
  const filterConditions = getFilterConditionsByOptionName(HELPDESK_CUSTOM_FIELD, props.options)
  return filterConditions.map((c) => addLabel(c))
}

const selectedFilters = computed(() => {
  // wait for selected fields to be fetched before rendering, otherwise label will end up as empty string and we'll have duplicate fields
  return selectedFieldsFetched.value ? props.addedFilters : []
})

const allAddedFilters = computed(() => {
  return [...selectedFilters.value, filterBeingAdded.value].filter(Boolean) as DeconstructedOption[]
})

const filterGroupItems = computed(() => {
  const selectedFilterGroupItems = selectedFilters.value.map(toFilterGroupItem)
  return [...selectedFilterGroupItems, translatedFilterBeingAdded.value].filter(Boolean) as AddedFilter[]
})

const clearFilterBeingAdded = () => {
  filterBeingAdded.value = null
  translatedFilterBeingAdded.value = null
}

const filterAddingStarted = (obj: any) => {
  filterBeingAdded.value = getEmptyFilterOption()

  filterBeingAdded.value.option = obj
  translatedFilterBeingAdded.value = toFilterGroupItem(filterBeingAdded.value)
  emit('filter-adding-started', obj)
}

const handleRemove = (id: string) => {
  if (filterBeingAdded.value?.id === id) {
    clearFilterBeingAdded()
  } else {
    emit('remove', id)
  }
}

const handleFilterApplied = (id: string, f: FilterItemEmit) => {
  const filter = cloneDeep(allAddedFilters.value.find((f) => f.id === id))
  filter.condition = f.selectedCondition
  filter.values = f.value

  if (filterBeingAdded.value?.id === id) {
    clearFilterBeingAdded()
  }

  emit('filter-applied', filter)
}
</script>
